// src/pages/Agenda.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Paper, TextField, MenuItem, Tooltip, IconButton, Button } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { format } from 'date-fns';
import { DndContext, PointerSensor, useSensor, useSensors, closestCenter, DragOverlay } from '@dnd-kit/core';

import { useAgenda } from '../hooks/useAgenda';
import { useBloqueos, etiquetaMotivoBloqueo } from '../hooks/useBloqueos';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import AgendaGrid from '../components/AgendaGrid';
import AgendaListaMovil from '../components/AgendaListaMovil';
import PantallaCargaMedica from '../components/common/PantallaCargaMedica';
import ColaPacientes from '../components/ColaPaciente';
import NuevaConsulta from '../components/NuevaConsulta';
import NuevoPaciente from '../components/NuevoPaciente';
import ModalNuevaFactura from '../components/facturacion/ModalNuevaFactura';
import ModalBloquearHorario from '../components/agenda/ModalBloquearHorario';

import RefreshIcon from '@mui/icons-material/Refresh';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import EventBusyOutlinedIcon from '@mui/icons-material/EventBusyOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';

const HORAS_LABORABLES = [
    '08:00', '08:30', '09:00', '09:30', '10:00', '10:30', '11:00', '11:30',
    '12:00', '12:30', '13:00', '13:30', '14:00', '14:30','15:00','15:30', '16:00','16:30', '17:00','17:30',
    '18:00', '18:30', '19:00', '19:30', '20:00', '20:30', '21:00'
];

export default function Agenda() {
    const navigate = useNavigate();
    const { veHistoriaClinica } = useAuth();
    // Recepción no entra en la estación clínica: al pulsar una cita abre la ficha del paciente.
    const abrirCita = (cita) => {
        if (veHistoriaClinica) return navigate(`/workspace/${cita.id || cita._id}`);
        const pacId = cita.paciente?.id || cita.paciente?._id || cita.paciente;
        if (pacId) navigate(`/pacientes/${pacId}`);
    };

    // 1. LÓGICA DE DOMINIO (Hook)
    const {
        fechaSeleccionada, setFechaSeleccionada,
        medicos, medicoSeleccionado, setMedicoSeleccionado,
        citas, loading, isInitialLoad,
        activeDragCita,
        handleDragStart, handleDragCancel, handleDragEnd,
        fetchAgendaDiaria, esAdmin, user
    } = useAgenda();

    // 2. ESTADOS DE MODALES Y UI LOCAL
    const [openNuevaConsulta, setOpenNuevaConsulta] = useState(false);
    const [openNuevoPaciente, setOpenNuevoPaciente] = useState(false);
    const [citaParaCobrar, setCitaParaCobrar] = useState(null);
    const [huecoPreseleccionado, setHuecoPreseleccionado] = useState(null);
    const [openBloqueo, setOpenBloqueo] = useState(false);

    // Bloqueos de agenda (vacaciones, festivos…) del día visible
    const { showToast } = useToast();
    const { bloqueos, cargarBloqueos, obtenerBloqueo } = useBloqueos(fechaSeleccionada);
    // Recepción y dirección quitan cualquiera; un médico solo los de su agenda
    const puedeQuitarBloqueo = (b) => esAdmin || (b.profesional && String(b.profesional) === String(user?.id));
    const quitarBloqueo = async (b) => {
        if (!window.confirm(`¿Quitar el bloqueo "${etiquetaMotivoBloqueo(b)}"? Se podrán volver a dar citas en ese periodo.`)) return;
        try {
            await api.delete(`/bloqueos/${b.id}`);
            showToast('Bloqueo quitado', 'success');
            cargarBloqueos();
        } catch (err) {
            showToast(err?.message || 'No se pudo quitar el bloqueo', 'error');
        }
    };

    // 🎟️ Ausente SÍ consume la sesión del bono (bono.service._usos cuenta las citas
    // en estado 'ausente'). Se confirma siempre, y cuando la cita lleva bono se
    // avisa de que la sesión se pierde y de que "Cancelar" es la otra opción.
    const marcarAusente = async (cita) => {
        const paciente = `${cita?.paciente?.nombre || ''} ${cita?.paciente?.apellidos || ''}`.trim();
        const aviso = cita?.bono
            ? `¿Marcar como AUSENTE${paciente ? ` a ${paciente}` : ''}?\n\n`
              + '⚠️ Esta cita va con BONO: la sesión se dará por GASTADA y no se le devolverá al paciente.\n\n'
              + 'Si avisó de que no venía, cancela la cita en su lugar: así conserva la sesión.'
            : `¿Marcar como AUSENTE${paciente ? ` a ${paciente}` : ''}?\n\n`
              + 'Queda registrado como falta de asistencia.\n\n'
              + 'Si avisó de que no venía, cancela la cita en su lugar.';
        if (!window.confirm(aviso)) return;
        try {
            await api.patch(`/consultas/${cita.id || cita._id}/estado`, { estado: 'ausente' });
            showToast('Paciente marcado como ausente.', 'info');
            fetchAgendaDiaria(true);
        } catch (err) {
            showToast(err?.message || 'No se pudo marcar la ausencia', 'error');
        }
    };

    // 🎟️ Cancelar una cita la saca de la agenda SIN consumir sesión de bono: el
    // saldo se calcula contando las citas no canceladas (bono.service._usos), así
    // que basta con el cambio de estado. "Ausente" sí la gasta, por eso se avisa.
    const cancelarCita = async (cita) => {
        const paciente = `${cita?.paciente?.nombre || ''} ${cita?.paciente?.apellidos || ''}`.trim();
        const aviso = `¿Cancelar la cita${paciente ? ` de ${paciente}` : ''}?\n\n`
            + 'Se libera el hueco de la agenda y, si la cita iba con bono, la sesión vuelve al paciente.\n\n'
            + 'Si el paciente no se presentó y quieres que la sesión cuente como gastada, márcala como "Ausente".';
        if (!window.confirm(aviso)) return;
        try {
            await api.patch(`/consultas/${cita.id || cita._id}/estado`, { estado: 'cancelada' });
            showToast('Cita cancelada. Si iba con bono, la sesión vuelve a estar disponible.', 'success');
            fetchAgendaDiaria(true);
        } catch (err) {
            showToast(err?.message || 'No se pudo cancelar la cita', 'error');
        }
    };

    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

    // Datos compartidos por la rejilla (escritorio) y la lista (móvil)
    const medicosVisibles = medicoSeleccionado === 'todos'
        ? medicos
        : medicos.filter(m => String(m.id) === String(medicoSeleccionado));

    const nombreProfesional = (id) => {
        const m = medicos.find(x => String(x.id) === String(id));
        return m ? `${m.nombre} ${m.apellidos}`.trim() : 'Profesional';
    };
    const bloqueosVisibles = bloqueos.filter(b => b.profesional === null || medicosVisibles.some(m => String(m.id) === String(b.profesional)));
    const textoPeriodo = (b) => {
        const ini = new Date(b.inicio);
        const fin = new Date(b.fin);
        const mismoDia = format(ini, 'yyyy-MM-dd') === format(new Date(fin.getTime() - 1), 'yyyy-MM-dd');
        const diaCompleto = format(ini, 'HH:mm') === '00:00' && format(fin, 'HH:mm') === '00:00';
        if (mismoDia) return diaCompleto ? 'Todo el día' : `${format(ini, 'HH:mm')} - ${format(fin, 'HH:mm')}`;
        if (diaCompleto) return `Del ${format(ini, 'dd/MM')} al ${format(new Date(fin.getTime() - 1), 'dd/MM')}`;
        return `Del ${format(ini, 'dd/MM HH:mm')} al ${format(fin, 'dd/MM HH:mm')}`;
    };

    const obtenerCitaEspecifica = (hora, medId) => citas.find(c => {
        const horaCita = c.hora || (c.fecha && !isNaN(new Date(c.fecha)) ? format(new Date(c.fecha), 'HH:mm') : '');
        const profId = c.profesional?.id || c.profesional;
        return horaCita === hora && String(profId) === String(medId);
    });

    // 3. ORQUESTACIÓN VISUAL
    return (
        <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#f8fafc', minHeight: '100vh' }} className="animate-fade-in">
            {/* Cabecera Principal */}
            <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} sx={{ mb: 4 }}>
                <Box>
                    <Typography variant="h4" fontWeight={950} color="#0f172a" letterSpacing="-1.1px">Agenda</Typography>
                    <Typography variant="body2" color="text.secondary" fontWeight={600}>Citas de todos los profesionales, al día y en tiempo real</Typography>
                </Box>
                <Box display="flex" gap={1.5} alignItems="center" flexWrap="wrap">
                    <Button variant="outlined" startIcon={<PersonAddIcon />} onClick={() => setOpenNuevoPaciente(true)} sx={{ borderRadius: '10px', fontWeight: 'bold' }}>Alta Paciente</Button>
                    <Button variant="outlined" color="inherit" startIcon={<EventBusyOutlinedIcon />} onClick={() => setOpenBloqueo(true)} sx={{ borderRadius: '10px', fontWeight: 'bold', color: '#475569', borderColor: '#cbd5e1' }}>Bloquear horario</Button>
                    <Button variant="contained" startIcon={<CalendarTodayIcon />} onClick={() => setOpenNuevaConsulta(true)} sx={{ bgcolor: '#0f172a', borderRadius: '10px', fontWeight: 'bold', '&:hover': { bgcolor: '#1e293b' } }}>Nueva cita</Button>
                </Box>
            </Box>

            {/* Barra de Controles (Filtros) */}
            <Paper elevation={0} sx={{ p: 2.5, borderRadius: '20px', border: '1px solid #e2e8f0', display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mb: 4 }}>
                <DatePicker label="Día de la Agenda" value={fechaSeleccionada} onChange={(v) => v && setFechaSeleccionada(v)} />
                {esAdmin && (
                    <TextField select size="small" label="Filtrar Agenda Facultativa" value={medicoSeleccionado} onChange={(e) => setMedicoSeleccionado(e.target.value)} sx={{ minWidth: '260px' }}>
                        <MenuItem value="todos">Ver Agendas Unidas (Clínica Global)</MenuItem>
                        {medicos.map(m => <MenuItem key={m.id} value={m.id}>{m.nombre} {m.apellidos}</MenuItem>)}
                    </TextField>
                )}
                <Tooltip title="Sincronizar Manual">
                    <IconButton onClick={() => { fetchAgendaDiaria(true); cargarBloqueos(); }}><RefreshIcon /></IconButton>
                </Tooltip>
            </Paper>

            {/* Bloqueos del día (vacaciones, festivos, formación…) */}
            {bloqueosVisibles.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-4">
                    {bloqueosVisibles.map(b => (
                        <div key={b.id} className="inline-flex items-center gap-2 pl-3 pr-1.5 py-1.5 rounded-xl bg-slate-100 border border-slate-200 text-slate-600">
                            <EventBusyOutlinedIcon sx={{ fontSize: 18 }} />
                            <span className="text-xs font-black">{etiquetaMotivoBloqueo(b)}</span>
                            <span className="text-xs font-semibold text-slate-500">
                                {b.profesional ? nombreProfesional(b.profesional) : 'Toda la clínica'} · {textoPeriodo(b)}
                            </span>
                            {puedeQuitarBloqueo(b) ? (
                                <Tooltip title="Quitar bloqueo">
                                    <IconButton size="small" onClick={() => quitarBloqueo(b)} aria-label="Quitar bloqueo">
                                        <DeleteOutlineOutlinedIcon sx={{ fontSize: 18 }} />
                                    </IconButton>
                                </Tooltip>
                            ) : <span className="w-1.5" />}
                        </div>
                    ))}
                </div>
            )}

            {/* Área Principal (Cuadrícula y Sala de Espera) */}
            <div className="flex flex-col xl:flex-row gap-6 items-start w-full">
                <div className="flex-1 w-full min-w-0">
                    {isInitialLoad ? (
                        <div className="bg-white border border-slate-200 rounded-[24px]">
                            <PantallaCargaMedica variante="agenda" />
                        </div>
                    ) : (
                        <>
                            {/* 🖥️ ESCRITORIO: rejilla completa con arrastrar y soltar */}
                            <div className="hidden lg:block overflow-x-auto">
                                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={handleDragStart} onDragEnd={handleDragEnd} onDragCancel={handleDragCancel}>
                                    <Box sx={{ opacity: loading ? 0.7 : 1, transition: 'opacity 0.2s', width: '100%' }}>
                                        <AgendaGrid
                                            medicosVisibles={medicosVisibles}
                                            horasLaborables={HORAS_LABORABLES}
                                            citas={citas}
                                            obtenerCitaEspecifica={obtenerCitaEspecifica}
                                            fecha={fechaSeleccionada}
                                            onAbrirEstacion={abrirCita}
                                            onContextMenu={(e, h, mId) => {
                                                setHuecoPreseleccionado({ hora: h, profesionalId: mId });
                                                setOpenNuevaConsulta(true);
                                            }}
                                            onVerExpediente={(pacId) => navigate(`/pacientes/${pacId}`)}
                                            onCobrarConsulta={(cita) => setCitaParaCobrar(cita)}
                                            onCancelarCita={cancelarCita}
                                            onMarcarAusente={marcarAusente}
                                            obtenerBloqueo={obtenerBloqueo}
                                            puedeQuitarBloqueo={puedeQuitarBloqueo}
                                            onQuitarBloqueo={quitarBloqueo}
                                        />
                                    </Box>

                                    <DragOverlay dropAnimation={{ duration: 250, easing: 'cubic-bezier(0.18, 0.67, 0.6, 1.22)' }}>
                                        {activeDragCita ? (
                                            <div className="w-[310px] h-[120px] p-3 rounded-2xl border shadow-2xl flex flex-col justify-between cursor-grabbing bg-white/95 backdrop-blur-md border-blue-400 text-slate-800 ring-4 ring-blue-500/30 rotate-3 scale-105 transition-transform" style={{ margin: 0 }}>
                                                <div className="flex justify-between items-start gap-2">
                                                    <div className="overflow-hidden flex-1">
                                                        <h4 className="text-sm font-black truncate tracking-tight uppercase text-blue-900">
                                                            {activeDragCita.paciente?.nombre} {activeDragCita.paciente?.apellidos}
                                                        </h4>
                                                        <span className="text-[10px] font-bold opacity-70 tracking-wider font-mono block mt-0.5">Moviendo Turno...</span>
                                                    </div>
                                                    <span className="w-2.5 h-2.5 rounded-full shrink-0 mt-1 bg-blue-500 animate-pulse shadow-[0_0_8px_rgba(59,130,246,0.8)]" />
                                                </div>
                                                <p className="text-xs font-semibold opacity-70 line-clamp-2 leading-relaxed my-1 flex-1">{activeDragCita.motivo || 'Consulta médica'}</p>
                                            </div>
                                        ) : null}
                                    </DragOverlay>
                                </DndContext>
                            </div>

                            {/* 📱 MÓVIL / TABLET: lista tipo app (sin scroll horizontal) */}
                            <div className="lg:hidden">
                                <AgendaListaMovil
                                    medicosVisibles={medicosVisibles}
                                    horasLaborables={HORAS_LABORABLES}
                                    obtenerCitaEspecifica={obtenerCitaEspecifica}
                                    fecha={fechaSeleccionada}
                                    onAbrirEstacion={abrirCita}
                                    onNuevaCita={(hora, medId) => { setHuecoPreseleccionado({ hora, profesionalId: medId }); setOpenNuevaConsulta(true); }}
                                    onVerExpediente={(pacId) => navigate(`/pacientes/${pacId}`)}
                                    onCobrarConsulta={(cita) => setCitaParaCobrar(cita)}
                                    onCancelarCita={cancelarCita}
                                    onMarcarAusente={marcarAusente}
                                    obtenerBloqueo={obtenerBloqueo}
                                />
                            </div>
                        </>
                    )}
                </div>

                {/* Sala de Espera Lateral */}
                {!isInitialLoad && medicoSeleccionado !== 'todos' && (
                    <div className="w-full xl:w-[380px] shrink-0 xl:sticky xl:top-6">
                        <ColaPacientes
                            citas={citas}
                            idMedicoLogueado={medicoSeleccionado !== 'todos' ? medicoSeleccionado : user.id}
                            fetchAgendaDiaria={fetchAgendaDiaria}
                            esVistaMultiSala={false}
                        />
                    </div>
                )}
            </div>

            {/* =========================================================
                📦 CAPA DE MODALES
               ========================================================= */}
            <ModalNuevaFactura
                open={Boolean(citaParaCobrar)}
                onClose={() => setCitaParaCobrar(null)}
                citaPrevia={citaParaCobrar}
                onSuccess={() => { setCitaParaCobrar(null); fetchAgendaDiaria(true); }}
            />

            <NuevaConsulta
                open={openNuevaConsulta}
                onClose={() => { setOpenNuevaConsulta(false); setHuecoPreseleccionado(null); }}
                onSuccess={() => fetchAgendaDiaria(true)}
                horaPredeterminada={huecoPreseleccionado?.hora}
                profesionalIdPredeterminado={huecoPreseleccionado?.profesionalId}
            />

            <ModalBloquearHorario
                open={openBloqueo}
                onClose={() => setOpenBloqueo(false)}
                onSuccess={() => cargarBloqueos()}
                fecha={fechaSeleccionada}
                medicos={medicos}
                esAdmin={esAdmin}
                user={user}
            />

            <NuevoPaciente
                open={openNuevoPaciente}
                onClose={() => setOpenNuevoPaciente(false)}
                onSuccess={() => fetchAgendaDiaria(true)}
            />
        </Box>
    );
}